/**
 * ConsoleShell — layout chrome for the Platform Console area.
 *
 * Structure: topbar (area title, org/user identity from the session
 * accessor, theme toggle) + left nav rail (>= lg) and a grouped section
 * menu on narrow viewports. Every section is its
 * own route (deep-linkable); active state is exact-match except the
 * Fonti entry which stays lit on its detail child.
 *
 * The rail MIRRORS the product sidebar (``ConversationMiniRail`` + its
 * host in ``routes/_authed/route.tsx``) so navigation feels identical on
 * both surfaces — same mechanism, not a lookalike:
 *
 *   - ONE bar that is NOT permanently expanded: a 56px icon rail that
 *     widens to a labelled bar; the parent animates ``width`` in CSS
 *     (Claude / shadcn collapsible pattern), never a rail + panel pair.
 *   - Every row is ``[icon][label]`` with the icon centred in a FIXED
 *     56px cell = the collapsed width, so the icon never moves — only
 *     the text reveals as the clip widens.
 *   - The expand/collapse row carries the same ``PanelLeftOpen`` /
 *     ``PanelLeftClose`` affordance and the same copy as the product.
 *   - Collapsed rows surface their label through a right-side tooltip.
 *   - The open/closed state is PERSISTED (zustand ``persist`` over the
 *     shared ``zustandStorage``). New Console sessions start expanded so
 *     section names are visible; a chosen collapsed state stays persisted.
 *
 * What stays console-specific: the ``--con-*`` token layer and the
 * active-item treatment (neutral ink tint + a 2px orange left indicator,
 * NO orange fills, NO card background behind the rail) — the product
 * paints its active rows orange, the console must not.
 *
 * Why not ``PlatformAreaLayout``: that shell is styled with the STATIC
 * light-only tokens (``text-ink-*`` / ``bg-cream``) and has no collapse
 * behavior — it cannot render on graphite. The console rail replicates
 * its interaction contract (aria-current, focus rings, exact matching)
 * on the scoped ``--con-*`` tokens instead.
 */

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  AppWindow,
  BarChart3,
  BookOpen,
  Cable,
  ChevronDown,
  Cpu,
  CreditCard,
  FileText,
  Gauge,
  GitBranch,
  KeyRound,
  LockKeyhole,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Table2,
  TerminalSquare,
  Users,
  WalletCards,
  Workflow,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import type * as React from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { getSession } from "@ando/core/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LogoutOverlay,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@ando/ui";

import { logoutAndRedirect } from "@/features/auth/session";
import { initialsOf } from "@/lib/initials";
import { zustandStorage } from "@/lib/storage";

import { surfaceAccess } from "../access";
import { consoleContextQueryOptions } from "../api/queries";
import { consoleCreditsQueryOptions } from "../api/queries";
import { UKS } from "../lib/brand";
import { formatUsdCents } from "../lib/format";
import { setPreferredSurface } from "../lib/surface";
import { ConsoleThemeToggle, useConsoleThemeClass } from "../theme/ConsoleTheme";
import { ConsoleCommandCenter } from "./CommandCenter";
import { conFocusRing } from "./atoms";

/**
 * Collapsed rail width. Same 56px as the product bar, and the exact width
 * of the icon cell inside every row — the label starts at the clip edge so
 * it is fully hidden while collapsed.
 */
const RAIL_COLLAPSED = "w-14";
/**
 * Expanded rail width. The product opens to 384px because it embeds the
 * conversation list; the console rail has no list under it, so it opens to
 * the width its labels need (256px) and keeps the console's denser rhythm.
 */
const RAIL_EXPANDED = "w-64";

/**
 * Rail rows are exactly as wide as the bar, which clips its overflow to
 * hide the labels — an OFFSET focus ring would be clipped with them. Keep
 * the same 2px ring, drawn inside the row.
 */
const conRailFocusRing = "focus-visible:ring-inset focus-visible:ring-offset-0";

/** Versioned key — mirrors ``ando.chat-sidebar.v1`` on the product side. */
export const CONSOLE_SIDEBAR_STORAGE_KEY = "ando.console-sidebar.v1";

export interface ConsoleSidebarState {
  /** True = labelled bar (256px), false = icon rail (56px). */
  isExpanded: boolean;
  toggleExpanded: () => void;
  setExpanded: (value: boolean) => void;
}

/**
 * Console rail open state — the console counterpart of the product's
 * ``useSidebarStore``. It lives in this module (not a store file of its
 * own) because it is chrome state owned exclusively by this shell; the
 * persistence adapter is the SAME ``zustandStorage`` the product uses, so
 * both surfaces behave identically across reloads and Tauri targets.
 */
export const useConsoleSidebarStore = create<ConsoleSidebarState>()(
  persist(
    (set) => ({
      // Show navigation labels until the person chooses to collapse them.
      isExpanded: true,
      toggleExpanded: () => set((s) => ({ isExpanded: !s.isExpanded })),
      setExpanded: (value) => set({ isExpanded: value }),
    }),
    {
      name: CONSOLE_SIDEBAR_STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => zustandStorage),
    },
  ),
);

export const selectConsoleSidebarExpanded = (s: ConsoleSidebarState): boolean => s.isExpanded;

export interface ConsoleNavItem {
  readonly to: string;
  readonly label: MessageDescriptor;
  readonly icon: LucideIcon;
  /** Keep the entry active on child routes (e.g. connector detail). */
  readonly fuzzy?: boolean;
  /** Hide organisation administration from non-manager members. */
  readonly managerOnly?: boolean;
}

/**
 * A captioned cluster of nav rows. The caption reuses the rail's existing
 * caption + hairline mechanic: the label shows while the bar is expanded, a
 * plain hairline stands in once it is clipped to the icons.
 */
export interface ConsoleNavGroup {
  readonly caption: MessageDescriptor;
  readonly items: readonly ConsoleNavItem[];
  /**
   * Manager-only group — visible solely to a "Gestore" (org owner/admin,
   * ``can_manage: true``). A "Sviluppatore" (member) never sees it in the
   * rail, the mobile menu or the ⌘K palette. Gated in ``ConsoleShell`` off
   * ``GET /context``; the Account-group routes repeat the check for deep links.
   */
  readonly managerOnly?: boolean;
}

/**
 * The console sections, grouped around the five jobs a user performs rather
 * than around the product's internal feature taxonomy. Every existing route
 * remains available by URL; temporarily hidden pages are omitted here.
 *
 * Home = understand readiness and finish setup.
 * Knowledge = connect and inspect Engine sources, including personal accounts.
 * Build = compose and test what Engine should do.
 * Applications = configure an app before managing its users and credentials.
 * Organization = manage Console collaborators, distinct from app users.
 * Operate = monitor spend and limits.
 *
 * Ordering inside a group is the reading order of the rail, mobile menu and
 * command palette projection.
 */
const NAV_GROUPS: readonly ConsoleNavGroup[] = [
  {
    caption: msg`Home`,
    items: [
      { to: "/console", label: msg`Overview`, icon: LayoutDashboard },
      { to: "/console/get-started", label: msg`Inizia`, icon: Zap },
    ],
  },
  {
    caption: msg`Knowledge`,
    items: [
      { to: "/console/connectors", label: msg`Fonti`, icon: Cable, fuzzy: true },
      { to: "/console/documents", label: msg`Documenti`, icon: FileText },
      { to: "/console/access", label: msg({ id: "console.resourceAccessNav", message: "Accessi" }), icon: LockKeyhole },
      { to: "/console/schema", label: msg`Schema (${UKS})`, icon: Table2 },
    ],
  },
  {
    caption: msg`Build`,
    items: [
      { to: "/console/playground", label: msg`Playground`, icon: TerminalSquare },
      { to: "/console/flows", label: msg`Flows`, icon: Workflow, fuzzy: true },
      { to: "/console/actions", label: msg`Azioni`, icon: Zap },
      { to: "/console/models", label: msg`Modelli`, icon: Cpu },
    ],
  },
  {
    caption: msg`Applications`,
    items: [
      { to: "/console/apps", label: msg`App`, icon: AppWindow },
      { to: "/console/embedded-identity", label: msg`Utenti delle app`, icon: Users },
      { to: "/console/keys", label: msg`AppKeys`, icon: KeyRound, fuzzy: true },
      { to: "/console/developers", label: msg`Il tuo agente`, icon: Plug },
      { to: "/console/docs", label: msg`Docs`, icon: BookOpen },
    ],
  },
  {
    caption: msg`Organization`,
    items: [
      { to: "/console/members", label: msg`Team Console`, icon: Users, managerOnly: true },
    ],
  },
  {
    caption: msg`Operate`,
    items: [
      { to: "/console/traces", label: msg`Richieste e trace`, icon: GitBranch },
      { to: "/console/usage", label: msg`Usage`, icon: BarChart3 },
      { to: "/console/limits", label: msg`Limiti`, icon: Gauge },
      { to: "/console/billing", label: msg`Fatturazione`, icon: CreditCard, managerOnly: true },
    ],
  },
];

function isNavActive(pathname: string, item: ConsoleNavItem): boolean {
  // Deterministic pathname match (not ``useMatchRoute``, whose loosely-typed
  // ``to`` could light up every row): a non-fuzzy row is active ONLY on its
  // exact path (so Overview ``/console`` never stays lit on a child page); a
  // fuzzy row is active on its path or any descendant. Distinct, non-nested
  // ``to``s mean exactly ONE row is ever active — the orange indicator lives
  // only in the section you're in.
  if (item.to === "/console/connectors" && pathname === "/console/accounts") return true;
  if (
    item.to === "/console/access" &&
    (pathname.startsWith("/console/connection-access/") ||
      pathname.startsWith("/console/document-access/"))
  ) return true;
  if (item.fuzzy) {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }
  return pathname === item.to;
}

function useIsNavActive(item: ConsoleNavItem): boolean {
  return isNavActive(useLocation().pathname, item);
}

/**
 * Reveal a collapsed row's label on hover/focus — the product rail's
 * mechanism (Radix tooltip, ``side="right"``). Expanded rows return the
 * trigger untouched: the label is already on screen.
 *
 * The content is PORTALED outside the console root, so it is stamped with
 * ``useConsoleThemeClass()`` and repainted on the ``--con-*`` tokens (same
 * rule as ``ConModal`` / the user menu).
 *
 * The ``TooltipProvider`` is the single one mounted at the authed shell
 * root — the console renders inside it (``_authed/_modal/console``), and a
 * second provider would break Radix's global skip-delay window.
 */
function ConsoleRailTooltip({
  label,
  enabled,
  children,
}: {
  label: string;
  enabled: boolean;
  children: React.ReactElement;
}): React.ReactElement {
  const themeClass = useConsoleThemeClass();
  if (!enabled) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={8}
        className={cn(
          themeClass,
          "con-pop border border-[color:var(--con-border)]",
          "bg-[color:var(--con-surface-raised)] font-medium text-[color:var(--con-on-surface)] text-[length:var(--con-text-xs)]",
        )}
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function ConsoleNavLink({
  item,
  variant,
  expanded = true,
}: {
  item: ConsoleNavItem;
  variant: "rail" | "strip";
  /** Rail only — false clips the label to the 56px icon cell. */
  expanded?: boolean;
}): React.ReactElement {
  const { i18n } = useLingui();
  const active = useIsNavActive(item);
  const Icon = item.icon;
  const label = i18n._(item.label);
  const isRail = variant === "rail";
  const link = (
    <Link
      to={item.to as never}
      aria-current={active ? "page" : undefined}
      aria-label={isRail ? label : undefined}
      data-active={active ? "true" : "false"}
      // Inline style is deliberate: text-on-page ONLY — no background, no
      // border, no box, in ANY state. The active state is expressed solely
      // by the 2px indicator + ink contrast. Inline wins over any stray CSS.
      style={{ background: "transparent", border: 0 }}
      className={cn(
        "con-nav-link relative flex items-center text-[length:var(--con-text-sm)]",
        "transition-colors duration-150 ease-out",
        conFocusRing,
        isRail
          ? // The row is exactly the collapsed width while collapsed, so the
            // tooltip anchors BESIDE the icon instead of where the (clipped)
            // label would be — same fix the product rail carries.
            cn("h-9 shrink-0", conRailFocusRing, expanded ? "w-full pr-2.5" : RAIL_COLLAPSED)
          : "min-h-11 shrink-0 gap-2 whitespace-nowrap px-2.5",
        active
          ? "font-medium text-[color:var(--con-on-surface)]"
          : "font-normal text-[color:var(--con-on-surface-muted)] hover:text-[color:var(--con-on-surface)]",
        // Strip has no room for the rail's left indicator: the active row is
        // marked by a 2px accent underline instead — same single orange, just
        // rotated to the bottom edge.
        !isRail &&
          active &&
          "after:absolute after:inset-x-2.5 after:bottom-0 after:h-0.5 after:rounded-full after:bg-[color:var(--con-accent)]",
      )}
    >
      {/* 2px orange left indicator — the ONLY orange in the nav. */}
      {isRail && active ? (
        <span
          aria-hidden
          className="con-nav-indicator absolute top-1.5 bottom-1.5 left-0 w-0.5 rounded-full bg-[color:var(--con-accent)]"
        />
      ) : null}
      {/* Fixed 56px icon cell = the collapsed width: the icon is centred
          while collapsed and never shifts when the bar opens. */}
      <span
        className={
          isRail
            ? cn("flex h-full shrink-0 items-center justify-center", RAIL_COLLAPSED)
            : undefined
        }
      >
        <Icon
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className={cn(
            "shrink-0 transition-colors duration-150 ease-out",
            active
              ? "text-[color:var(--con-on-surface)]"
              : "text-[color:var(--con-on-surface-faint)]",
          )}
        />
      </span>
      <span className={cn("truncate", isRail && "whitespace-nowrap")}>{label}</span>
    </Link>
  );
  if (!isRail) return link;
  return (
    <ConsoleRailTooltip label={label} enabled={!expanded}>
      {link}
    </ConsoleRailTooltip>
  );
}

/**
 * Expand/collapse row — same affordance, same copy and same position
 * (first row of the bar) as the product rail's toggle.
 */
function ConsoleRailToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const { i18n } = useLingui();
  const label = expanded ? i18n._(msg`Comprimi la sidebar`) : i18n._(msg`Espandi la sidebar`);
  return (
    <ConsoleRailTooltip label={label} enabled={!expanded}>
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={expanded}
        data-testid="console-rail-expand"
        className={cn(
          "relative flex h-9 shrink-0 items-center font-normal text-[length:var(--con-text-sm)]",
          "text-[color:var(--con-on-surface-muted)] transition-colors duration-150 ease-out",
          "hover:text-[color:var(--con-on-surface)]",
          conFocusRing,
          conRailFocusRing,
          expanded ? "w-full pr-2.5" : RAIL_COLLAPSED,
        )}
      >
        <span
          className={cn(
            "flex h-full shrink-0 items-center justify-center text-[color:var(--con-on-surface-faint)]",
            RAIL_COLLAPSED,
          )}
        >
          {expanded ? (
            <PanelLeftClose size={16} strokeWidth={1.75} aria-hidden />
          ) : (
            <PanelLeftOpen size={16} strokeWidth={1.75} aria-hidden />
          )}
        </span>
        <span className="truncate whitespace-nowrap">{label}</span>
      </button>
    </ConsoleRailTooltip>
  );
}

/**
 * User menu — avatar-initials trigger, dropdown with identity + sign out.
 * The Radix portal renders OUTSIDE the console root, so the content is
 * stamped with ``useConsoleThemeClass()`` (same rule as ``ConModal``).
 */
function ConsoleUserMenu({
  displayName,
  email,
}: {
  displayName: string;
  email: string;
}): React.ReactElement {
  const { i18n } = useLingui();
  const themeClass = useConsoleThemeClass();
  // Logout takeover (Aug 27, 2026) — same branded wait as the workspace menu.
  const [loggingOut, setLoggingOut] = useState(false);
  return (
    <>
      <LogoutOverlay
        open={loggingOut}
        label={i18n._(msg`Disconnessione in corso…`)}
        subline={i18n._(msg`A presto`)}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={i18n._(msg`Menu utente`)}
            className={cn(
              "flex size-7 shrink-0 select-none items-center justify-center rounded-full",
              "border border-[color:var(--con-border-strong)] bg-[color:var(--con-surface-sunken)]",
              "font-semibold text-[color:var(--con-on-surface)] text-[length:var(--con-text-2xs)] tracking-[0.02em]",
              "transition-colors duration-150 ease-out hover:bg-[color:var(--con-surface-hover)]",
              conFocusRing,
            )}
          >
            {initialsOf(displayName, email)}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          className={cn(
            themeClass,
            "con-pop min-w-52 rounded-md border-[color:var(--con-border)]",
            "bg-[color:var(--con-surface-raised)] text-[color:var(--con-on-surface)]",
          )}
        >
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="truncate font-medium text-[color:var(--con-on-surface)] text-[length:var(--con-text-xs)]">
              {displayName || email}
            </span>
            {displayName && email ? (
              <span className="truncate font-normal text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-xs)]">
                {email}
              </span>
            ) : null}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[color:var(--con-border)]" />
          <DropdownMenuItem
            onSelect={() => {
              setLoggingOut(true);
              void logoutAndRedirect();
            }}
            className={cn(
              "gap-2 text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-xs)]",
              "focus:bg-[color:var(--con-surface-hover)] focus:text-[color:var(--con-on-surface)]",
            )}
          >
            <LogOut size={14} strokeWidth={1.75} aria-hidden />
            {i18n._(msg`Esci`)}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

/**
 * Org identity chip — the org NAME when the session carries it, with the
 * technical id demoted to tooltip/secondary. When only the UUID exists it
 * is TRUNCATED (first block), never the raw 36-char string in the topbar.
 */
function OrgIdentity({
  orgId,
  orgName,
}: {
  orgId: string;
  orgName: string | undefined;
}): React.ReactElement | null {
  if (!orgName && !orgId) return null;
  const shortId = orgId.length > 8 ? `${orgId.slice(0, 8)}…` : orgId;
  return (
    <span className="inline-flex min-w-0 items-baseline gap-1.5" title={orgId || undefined}>
      <span className="truncate text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-xs)]">
        {orgName || shortId}
      </span>
      {orgName && orgId ? (
        <span className="hidden font-mono text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-2xs)] md:inline">
          {shortId}
        </span>
      ) : null}
    </span>
  );
}

function ConsoleBalanceChip(): React.ReactElement | null {
  const { i18n } = useLingui();
  const credits = useQuery(consoleCreditsQueryOptions());
  if (!credits.data?.is_prepaid) return null;
  return (
    <Link
      to="/console/get-started"
      search={{} as never}
      className={cn(
        "hidden items-center gap-1.5 rounded-full border border-[color:var(--con-border-strong)] px-2.5 py-1 sm:inline-flex",
        "font-mono text-[color:var(--con-on-surface-muted)] text-[length:var(--con-text-xs)] hover:text-[color:var(--con-on-surface)]",
        conFocusRing,
      )}
      aria-label={i18n._(msg`Saldo crediti. Apri la ricarica`)}
    >
      <WalletCards size={14} aria-hidden />
      {formatUsdCents(i18n.locale, credits.data.balance_cents)}
    </Link>
  );
}

function ConsoleTopbar(): React.ReactElement {
  const { i18n } = useLingui();
  const session = getSession();
  // Dual-surface: product-entitled sessions get a way back to Ando; a
  // console-only (``platform-dev``) session sees NO product link anywhere —
  // for them the console IS the app. Logout is for everyone (the console
  // must be self-sufficient as a standalone surface).
  const canGoToProduct = surfaceAccess(session).product;
  return (
    <header className="con-topbar flex h-12 shrink-0 items-center justify-between gap-3 border-[color:var(--con-border)] border-b px-4">
      <div className="flex min-w-0 items-center gap-2.5">
        {canGoToProduct ? (
          <Link
            to="/"
            onClick={() => setPreferredSurface("product")}
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-sm px-1.5 py-1 font-medium text-[length:var(--con-text-xs)]",
              "text-[color:var(--con-on-surface-muted)] transition-colors duration-150 ease-out",
              "hover:text-[color:var(--con-on-surface)]",
              conFocusRing,
            )}
          >
            <ArrowLeft size={14} strokeWidth={1.75} aria-hidden />
            <span className="whitespace-nowrap">{i18n._(msg`Torna ad Ando`)}</span>
          </Link>
        ) : null}
        {canGoToProduct ? (
          <span aria-hidden className="text-[color:var(--con-border-strong)]">
            /
          </span>
        ) : null}
        <span className="whitespace-nowrap font-display font-semibold text-[color:var(--con-on-surface)] text-[length:var(--con-text-md)] tracking-[-0.012em]">
          {i18n._(msg`Ando Platform`)}
        </span>
        {session && (session.org_name || session.org_id) ? (
          <>
            <span aria-hidden className="text-[color:var(--con-border-strong)]">
              /
            </span>
            <OrgIdentity orgId={session.org_id} orgName={session.org_name} />
          </>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <ConsoleBalanceChip />
        <ConsoleThemeToggle />
        {session ? (
          <ConsoleUserMenu displayName={session.display_name ?? ""} email={session.email ?? ""} />
        ) : null}
      </div>
    </header>
  );
}

/**
 * Desktop rail (>= lg). ONE bar: the width animates between the 56px icon
 * rail and the labelled bar; ``overflow-hidden`` clips the labels so they
 * reveal as the bar opens (the icons never move).
 */
function ConsoleRail({ groups }: { groups: readonly ConsoleNavGroup[] }): React.ReactElement {
  const { i18n } = useLingui();
  const expanded = useConsoleSidebarStore(selectConsoleSidebarExpanded);
  const toggleExpanded = useConsoleSidebarStore((s) => s.toggleExpanded);
  return (
    <nav
      aria-label={i18n._(msg`Sezioni console`)}
      data-expanded={expanded}
      data-testid="console-rail"
      className={cn(
        "con-rail con-rail-surface hidden h-full shrink-0 flex-col gap-0.5 overflow-hidden border-[color:var(--con-border)] border-r pt-2 pb-3 lg:flex",
        expanded ? RAIL_EXPANDED : RAIL_COLLAPSED,
      )}
    >
      <ConsoleRailToggle expanded={expanded} onToggle={toggleExpanded} />
      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto [scrollbar-width:thin]">
        {/* One captioned cluster per user job. The caption begins in the
            label column, so expanded mode reads as a real two-level IA while
            the fixed icon column remains perfectly still during collapse. */}
        {groups.map((group) => (
          <section
            key={group.items.map((it) => it.to).join()}
            aria-label={i18n._(group.caption)}
            className="flex flex-col gap-0.5"
          >
            {expanded ? (
              <p className="truncate whitespace-nowrap pt-2.5 pr-3 pb-1 pl-14 font-semibold text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-2xs)] tracking-[0.045em]">
                {i18n._(group.caption)}
              </p>
            ) : (
              <div aria-hidden className="mx-3 my-1.5 h-px bg-[color:var(--con-border)]" />
            )}
            {group.items.map((item) => (
              <ConsoleNavLink key={item.to} item={item} variant="rail" expanded={expanded} />
            ))}
          </section>
        ))}
      </div>
      {/* Rail footer — quiet version / environment line. Collapsed it keeps
          only the version, centred in the same 56px cell as the icons. */}
      <div
        className={cn(
          "mt-auto flex shrink-0 items-center gap-2 pt-3",
          expanded ? "justify-between px-4" : cn("justify-center", RAIL_COLLAPSED),
        )}
      >
        {expanded ? (
          <span className="whitespace-nowrap text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-2xs)]">
            {i18n._(msg`Console`)}
          </span>
        ) : null}
        <span className="font-mono text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-2xs)]">
          {/* Environment name is technical vocabulary — not localized. */}
          {import.meta.env.DEV && expanded ? "v1 · dev" : "v1"}
        </span>
      </div>
    </nav>
  );
}

/** A compact mobile section picker with the same grouped routes as the rail. */
function ConsoleMobileMenu({
  groups,
}: {
  groups: readonly ConsoleNavGroup[];
}): React.ReactElement {
  const { i18n } = useLingui();
  const pathname = useLocation().pathname;
  const themeClass = useConsoleThemeClass();
  const activeGroup = groups.find((group) => group.items.some((item) => isNavActive(pathname, item)));
  const activeItem = activeGroup?.items.find((item) => isNavActive(pathname, item));
  const currentLabel = activeItem ? i18n._(activeItem.label) : i18n._(msg`Sezioni console`);
  return (
    <div className="con-mobile-strip shrink-0 border-[color:var(--con-border)] border-b px-3 py-1.5 lg:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`${i18n._(msg`Sezioni console`)}: ${currentLabel}`}
            className={cn(
              "flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-[length:var(--con-text-sm)]",
              "text-[color:var(--con-on-surface)] hover:bg-[color:var(--con-surface-hover)]",
              conFocusRing,
            )}
          >
            <span className="min-w-0 flex-1 truncate font-medium">{currentLabel}</span>
            {activeGroup ? (
              <span className="max-w-[35%] truncate text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-xs)]">
                {i18n._(activeGroup.caption)}
              </span>
            ) : null}
            <ChevronDown size={16} className="shrink-0 text-[color:var(--con-on-surface-muted)]" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={4}
          className={cn(
            themeClass,
            "con-pop max-h-[min(70vh,36rem)] w-[min(22rem,calc(100vw-1.5rem))] overflow-y-auto rounded-md border border-[color:var(--con-border)]",
            "bg-[color:var(--con-surface-raised)] p-1 text-[color:var(--con-on-surface)]",
          )}
        >
          {groups.map((group) => (
            <div key={group.items.map((item) => item.to).join()}>
              <DropdownMenuLabel className="px-3 pt-3 pb-1 font-semibold text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-xs)]">
                {i18n._(group.caption)}
              </DropdownMenuLabel>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isNavActive(pathname, item);
                return (
                  <DropdownMenuItem key={item.to} asChild className="p-0 focus:bg-[color:var(--con-surface-hover)]">
                    <Link
                      to={item.to as never}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex min-h-11 items-center gap-3 rounded-sm px-3 text-[length:var(--con-text-sm)]",
                        active ? "font-semibold text-[color:var(--con-on-surface)]" : "text-[color:var(--con-on-surface-muted)]",
                      )}
                    >
                      <Icon size={16} strokeWidth={1.75} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{i18n._(item.label)}</span>
                      {active ? <span aria-hidden className="size-1.5 rounded-full bg-[color:var(--con-accent)]" /> : null}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function ConsoleShell({ children }: { children: React.ReactNode }): React.ReactElement {
  const { i18n } = useLingui();
  const themeClass = useConsoleThemeClass();
  // Second gating dimension (orthogonal to surfaceAccess): the caller's ORG
  // role decides the in-console CAPABILITY tier. A "Sviluppatore" (member)
  // never sees the manager-only Organization / Operate entries — not in
  // the rail, the mobile menu, or the ⌘K palette. While the context query is
  // in flight ``can_manage`` is undefined, so ``canManage`` defaults to false
  // and manager-only entries stay hidden until it resolves true — no flash of
  // organisation controls for a member. Both the grouped rail and the flat projection (strip
  // + palette) derive from the SAME visible set, so a member can never reach a
  // hidden section by any nav path.
  const ctx = useQuery(consoleContextQueryOptions());
  const canManage = ctx.data?.can_manage === true;
  const [walletEmpty, setWalletEmpty] = useState(false);
  useEffect(() => {
    const show = (): void => setWalletEmpty(true);
    window.addEventListener("ando:engine-wallet-empty", show);
    return () => window.removeEventListener("ando:engine-wallet-empty", show);
  }, []);
  const visibleGroups = NAV_GROUPS.filter((group) => !group.managerOnly || canManage)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.managerOnly || canManage),
    }))
    .filter((group) => group.items.length > 0);
  const visibleItems = visibleGroups.flatMap((group) => group.items);
  return (
    <div
      className={cn(
        themeClass,
        "con-shell flex h-full w-full flex-col overflow-hidden bg-[color:var(--con-surface)] text-[color:var(--con-on-surface)]",
      )}
    >
      <ConsoleTopbar />
      {walletEmpty ? (
        <div
          role="alert"
          className="flex shrink-0 items-center justify-between gap-3 border-[color:var(--con-warning-text)] border-b bg-[color:var(--con-warning-soft)] px-4 py-2 text-[color:var(--con-warning-text)] text-[length:var(--con-text-sm)]"
        >
          <span>
            {i18n._(msg`Crediti esauriti. Ricarica il wallet per riprendere le chiamate Engine.`)}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Link
              to="/console/get-started"
              search={{} as never}
              onClick={() => setWalletEmpty(false)}
              className="font-semibold underline underline-offset-2"
            >
              {i18n._(msg`Ricarica`)}
            </Link>
            <button
              type="button"
              onClick={() => setWalletEmpty(false)}
              aria-label={i18n._(msg`Chiudi avviso`)}
              className={conFocusRing}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}
      <ConsoleMobileMenu groups={visibleGroups} />
      <div className="flex min-h-0 flex-1">
        <ConsoleRail groups={visibleGroups} />
        <main className="con-main-canvas min-w-0 flex-1 overflow-y-auto">{children}</main>
      </div>
      {/* Global overlays: ⌘K palette, ⌘I assistant, toasts — one mount for
          the whole console, inside the theme scope. */}
      <ConsoleCommandCenter navItems={visibleItems} />
    </div>
  );
}
